import React, {
	useEffect,
	useState,
	useMemo
} from 'react'

import {
	useDispatch,
	useSelector
} from 'react-redux'

import { DebounceInput } from "react-debounce-input"

import {
	setTemplatesModal,
	getProgramCategories,
	getPrograms,
	getProgramsChain,
	selectAllPrograms,
	selectAllCategories,
	createCategory,
	deleteCategory,
	createCategoryOption,
	deleteCategoryOption,
	updateCategory,
	updateCategoryOption,
	toggleCategoryEditor,
	deleteProgram,
	getSelectablePrograms,
	getActivePrograms,
	selectAllSelectablePrograms,
	selectAllActives,
	enrollInProgram,
	cancelProgram,
} from './programsSlice'

import { selectTrainerParam } from '../users/usersSlice'
import { useDebounceCallback } from '@react-hook/debounce'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from "react-bootstrap/Tooltip"
import Popover from 'react-bootstrap/Popover'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import DatePicker from "react-datepicker"
import Nav from "react-bootstrap/Nav"
import "react-datepicker/dist/react-datepicker.css"
import { Link, useHistory } from 'react-router-dom';
import Select from 'react-select'
import useCurrentUser from '../../hooks/useCurrentUser'
import { envAwarePost, dateToAPIStandard,slidingQueryComparision } from '../../util/util'

import Program from './Program/Program'
import SettingsEditor from './SettingsEditor/SettingsEditor'
import CategoryEditor from './CategoryEditor/CategoryEditor'
import AssignmentsEditor from './AssignmentsEditor/AssignmentsEditor'
import AssignModal from './AssignModal/AssignModal'
import CopyModal from './CopyModal/CopyModal'
import TemplatesModal from './TemplatesModal/TemplatesModal'
import DeleteModal from './DeleteModal/DeleteModal'
import useApiStatusDispatch from '../../hooks/useApiStatusDispatch'
import classes from './Programs.module.css'

import DiscussResourceButton from '../DiscussResourceButton/DiscussResourceButton'

const daysIn = (start_date) => {
	const date1 = new Date(start_date.replace(/-/g, '\/'))
	const date2 = new Date()
	const diffTime = Math.abs(date2 - date1)
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
	return diffDays
}

const Programs = props => {
	const history = useHistory()
	const urlParams = new URLSearchParams(window.location.search)
	const [filterId,setFilterId] = useState(parseInt(urlParams.get('program_id')))
	const user = useCurrentUser()
	const isClient = user.account_type === "client";
	const isGym = user.account_type === "gym";
	const isChain = user.account_type === 'chain'
	const partOfGym = parseInt(user.gym_id) !== 0
	const isOwner = (
		partOfGym ?
		parseInt(user.gym) === 1 :
		parseInt(user.trainer) === 1
	)
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch();
	const trainerParam = useSelector(selectTrainerParam)
	//filters
	const [filterOptions, setFilterOptions] = useState({})
	const [searchQuery,setSearchQuery] = useState("")

	const handleSearch = q => {
		setSearchQuery(q)
	}

	const debouncedHandleSearch = useDebounceCallback(handleSearch,300)

	const updateFilter = (category, option) => {
		const update = { ...filterOptions }
		update[category] = option
		setFilterOptions(update)
	}

	const removeFilter = (category) => {
		const update = { ...filterOptions }
		delete update[category]
		setFilterOptions(update)
	}

	const passesFilters = (program) => {
		const filterMatch = Object.keys(filterOptions).reduce((accum, category_id) => {
			const option_id = filterOptions[category_id]
			for (var i = program.categorization.length - 1; i >= 0; i--) {
				const optionData = program.categorization[i]
				if (
					Number(optionData.category_id) === Number(category_id) &&
					Number(optionData.option_id) === Number(option_id)
				) {
					return accum && true
				}
			}
			return false
		}, true)
		const ownerFilterMatch = (
			parseInt(program.user_id_number) === parseInt(ownerFilter.value) ||
			activeList === 'active' ||
			ownerFilter.value === 'all'
		)

		const urlFilterMatch = !Boolean(filterId) || program.programID === filterId

		return (
			urlFilterMatch && 
			filterMatch && 
			ownerFilterMatch
		)
	}

	const sortComparer = (a,b)=>{
		if(!Boolean(searchQuery)) return 0
		return slidingQueryComparision(a.programName,b.programName,searchQuery)
	}

	const [activeList, setActiveList] = useState('available')

	//categories
	const categoriesStatus = useSelector(state => state.programs.categories.status)
	const categories = useSelector(selectAllCategories)

	//selectable program
	const programsLoadingStatus = useSelector(state => state.programs.programs.status)
	const programs = useSelector(isClient?selectAllSelectablePrograms:selectAllPrograms)
	const selectableProgramsStatus = programsLoadingStatus
	const selectablePrograms = programs
	//map from user_id_number to user_name and account type
	const ownerMap = useMemo(()=>{
		const map = {}
		programs.forEach(program=>{
			map[program.user_id_number] = {
				owner_type:program.owner_type,
				user_name:program.owner_user_name
			}
		})
		return map
	},[programs]);
	//for clients default to top level account type
	//else default to current user's account type
	const [ownerFilter,setOwnerFilter] = useState(
		user?.account_type === 'gym' || isChain ?
		{value:user?.user_id_number,label:'My Programs'} :
		{value:'all',label:'All Programs'}
	)
	//reset all filters everytype program filter changes
	useEffect(()=>{setFilterOptions({})},[ownerFilter])

	const [hasASelectableProgram,setHasASelectableProgram] = useState(false)
	useEffect(() => {
		if(isClient){
			programs.forEach(program => {
				if(program.isClientSelectable){
					setHasASelectableProgram(true)
				}
			})
		} else {
			setHasASelectableProgram(true)
		}
	},[programs])

	//delete options state
	const [showDeleteModal, setShowDeleteModal] = useState(false)
	const [programToDelete, setProgramToDelete] = useState(null)

	//trigger data fetch once
	useEffect(() => {
		!isClient && dispatch(getPrograms())
		isClient && dispatch(getSelectablePrograms())
		dispatch(getProgramCategories())
		isClient && dispatch(getActivePrograms())
	}, [isClient])

	const ownerMapToSelectOptions = ()=>(
		Object.entries(ownerMap)
		.map(
			([id,accountData])=>({
				value:id,
				label:(
					id === user.user_id_number ?
					'My programs' : 
					`${accountData.user_name}'s programs`
				)//end ternary
			})//end cb
		)//end map
		.sort((a,b)=>{
			if(
				(parseInt(a.value) === parseInt(user.user_id_number)) ||
				(parseInt(b.value) === parseInt(user.user_id_number))
			){
				return (
					parseInt(a.value) === parseInt(user.user_id_number) ? 
					-1 :
					1
				)
			}else if(
				(parseInt(a.value) === parseInt(user.gym_id)) ||
				(parseInt(b.value) === parseInt(user.gym_id))
			){
				return (
					parseInt(a.user_id_number) === parseInt(user.gym_id) ? 
					-1 :
					1
				)
			}else{
				return (
					a.label < b.label ?
					-1 :
					1
				)
			}
		})
	)//end ownerMapToSelectOptions

	//filter state
	const [showFilterPopover, setShowFilterPopover] = useState(false)

	//active programs
	const activeProgramsStatus = useSelector(state => state.programs.activePrograms.status)
	const activePrograms = useSelector(selectAllActives)

	//enroll modal state
	const [showEnrollModal, setShowEnrollModal] = useState(false)
	const [enrollDate, setEnrollDate] = useState(new Date())
	const [programToAssign, setProgramToAssign] = useState(null)

	//delete assignment modal state
	const [showCancelModal, setShowCancelModal] = useState(false)
	const [programToCancel, setProgramToCancel] = useState(null);

	const list = {
		available: (
			selectablePrograms.length && hasASelectableProgram ?
				selectablePrograms.filter(passesFilters).sort(sortComparer).map(program => (
					<>
						{(!isClient || program.isClientSelectable) ?
						<div className="mt-3 border rounded p-3">
							<div className="d-flex justify-content-between align-items-center">
								<div>
									<h4 className="m-0">{program.programName}</h4>
									<small className="text-primary">Length: {program.programLength}days</small>
								</div>
								<div>
									<Button
										onClick={() => {
											setProgramToAssign(program.programID)
											setShowEnrollModal(true)
										}}
										variant="outline-primary"
										size="sm">
										Enroll
									</Button>
									<OverlayTrigger overlay={
										<Tooltip>Discuss this program in the chat</Tooltip>
									}>
										<span>
											<DiscussResourceButton
												className="btn btn-sm btn-outline-info m-1"
												name={program.programName}
												id={program.programID}
												type="program"
											>
												<i className="fa fa-comments"/>
											</DiscussResourceButton>
										</span>
									</OverlayTrigger>
								</div>
							</div>
							{Boolean(program.programNotes) && <p>{program.programNotes}</p>}
						</div>
					 :
					 null
						}
					</>
				)) :
				<div className="p-3 text-center">No programs available</div>
		),
		active: (
			activePrograms.length ?
				activePrograms.filter(passesFilters).sort(sortComparer).map(program => (
					<div key={"active" + program.assignment_id} className="mt-3 border rounded p-3">
						<div className=" d-flex justify-content-between">
							<div>
								<h4 className="m-0">{program.programName}</h4>
								<small className="text-primary">
									Day: {daysIn(program.start_date)}/{program.programLength}
								</small>
							</div>
							<div className="text-right ml-5" style={{ flex: '0 0 auto' }}>
								<div>Started: <span className="text-primary">{program.start_date}</span></div>
								<div>Ends: <span className="text-primary">{program.end_date}</span></div>
							</div>
						</div>
						{Boolean(program.programNotes) && <p>{program.programNotes}</p>}
						<div className="text-right">
							<Button
								onClick={() => {
									setShowCancelModal(true)
									setProgramToCancel(program.assignment_id)
								}}
								variant="outline-danger"
								size="sm">
								Cancel Program
							</Button>
						</div>
					</div>
				)) :
				<div className="p-3 text-center">You don't have any active programs</div>
		)
	}[activeList]
	const filters = (
		<>
			{(((!isClient)&&partOfGym)||isChain)&&
				<div className="my-1 mr-1" style={{width:'200px'}}>
					<Select 
						value={ownerFilter}
						onChange={ selectedOption => {
							setOwnerFilter(selectedOption)
						}}
						options={[
							{value:'all',label:'All Programs'},
							...ownerMapToSelectOptions(ownerMap)
						]} 
					/>
				</div>
			}
			{categories.filter(c=>c.owner_type === ownerFilter).map( category => (
				<div key={category.id} className="my-1 mr-1">
					<Form.Control size="sm" onChange={ (e) => {

						const option = e.target.value
						//console.log(option)
						if (option == 0) {
							removeFilter(category.id)
						} else {
							updateFilter(category.id, option)
						}
					}} as="select" custom>
						<option selected={!Boolean(filterOptions[category.id])} value={0} >
							Any {category.name}
						</option>
						{category.options.map(option => (
							<option key={option.id} selected={filterOptions[category.id] == option.id} value={option.id}>
								{option.name}
							</option>
						))}
					</Form.Control>
				</div>
			))}
		</>
	)

	const categoryFilter = (
		<div>
			<div className="d-flex align-items-end flex-wrap">
				<div className={isClient ? "d-flex align-items-center" : null}>
					<div className={isClient ? "mr-2" : null} >
						Filters:
						{isOwner && 
							<>
								<span
									style={{ cursor: 'pointer' }}
									className="text-primary ml-1 mr-2"
									onClick={() => dispatch(toggleCategoryEditor(true))
								}>
									<OverlayTrigger overlay={
										<Tooltip>
											Add/Edit Program Settings
										</Tooltip>
									}>
										<Link to={`/programSettings?${trainerParam}`}><i className="fas fa-cog"></i></Link>
									</OverlayTrigger>	
								</span>
								<span> (clients can also use these filters when looking for programs) </span>
							</>
						}
					</div>
					<div className="d-flex">
						{
							window.innerWidth > 1000 ?
								filters :
								<OverlayTrigger
									trigger="click"
									show={showFilterPopover}
									onHide={() => setShowFilterPopover(false)}
									overlay={
										<Popover>
											<Popover.Title as="h4">
												Filter Programs
											</Popover.Title>
											<Popover.Content>
												{filters}
											</Popover.Content>
										</Popover>
									}>
							<OverlayTrigger overlay={
								<Tooltip>
									Filter Programs
								</Tooltip>
							}>

									<Button
										onClick={() => setShowFilterPopover(!showFilterPopover)}
										size={isClient ? "md" : "sm"}
										variant={showFilterPopover ? "outline-danger" : "outline-primary"}>
										<i className={showFilterPopover ? "fas fa-times-circle" : "fas fa-filter"} />
									</Button>
								</OverlayTrigger>

								</OverlayTrigger>
						}
					</div>
				</div>
				{
					Object.keys(filterOptions).length > 0 &&
					<Button
						className="ml-auto"
						onClick={() => setFilterOptions({})}
						variant="outline-danger">
						Clear Filters
					</Button>
				}
			</div>
		</div>
	)

	return (
		<div className={[ classes.programsContainer].join(' ')}>
			<div className="pt-3 px-2 px-md-3">
				<div className="d-flex flex-wrap">
					<strong>Programs</strong>
					{!isClient && 
						<div className="ml-auto">
							<Button 
								onClick={()=>{
									dispatch(setTemplatesModal({show:true}))
								}}
								variant="outline-primary"
								className="mb-1 mr-1" 
								size="sm"
							>
								Use Program Template
							</Button>
							<Button 
								className="mb-1" 
								as={Link}
								variant="outline-success"
								to={`/programsAddEdit/?action=add&${trainerParam}`} 
								size="sm"
							>
								<i className="fa fa-plus d-sm-none mr-1"/>
								<span className="d-none d-sm-inline mr-1">
									Create New
								</span>
								Program
							</Button>
						</div>
					}
				</div>
				{isClient && <Nav variant="tabs" defaultActiveKey="/available">
					<Nav.Item onClick={() => setActiveList('available')}>
						<Nav.Link eventKey="/available">Enroll in Program</Nav.Link>
					</Nav.Item>
					<Nav.Item onClick={() => setActiveList('active')}>
						<Nav.Link eventKey="/active">Active Programs</Nav.Link>
					</Nav.Item>
				</Nav>}
				{!isClient && <small className="d-none d-sm-block mb-2">
					Multi-day Programs allow you to create Multi-day Plans / Programs with many workouts, nutrition plans, and tasks that you can quickly copy to specific clients. Not sure where to start? checkout our
						<a
							className="ml-1"
							target="_blank"
							href="https://www.fitsw.com/help/?s=programs&ht-kb-search=1&lang="
							rel="noreferrer"
						>
							how to guides
						</a>
				</small>}
			</div>
			<div>
				{
					[categoriesStatus, programsLoadingStatus, selectableProgramsStatus, activeProgramsStatus].includes('loading') ?
						<div className="d-flex p-3 justify-content-center">
							<Spinner animation="border" role="status">
								<span className="sr-only">Loading...</span>
							</Spinner>
						</div> :
						<div>
							<div className="px-2 px-md-3">
								<Form.Control 
									className="mb-2"
									onChange={ e => { debouncedHandleSearch(e.target.value) } }
									defaultValue={searchQuery}
									placeholder="search programs"
								/>
							</div>
							<div className="shadow bg-white p-3  border-top-0">
								{categoryFilter}
								{Boolean(filterId)&&
									<div className="mb-2">
										Viewing filtered list: 
										<Button 
											onClick={()=>{ 
												setFilterId(false)
												history.replace('/programs/')
											}}
											variant="outline-danger" 
											className="ml-2"
											size="sm"
										>
											Clear Filters
										</Button>
									</div>
								}
								{isClient && list}

								{!isClient && (
									programs.length ?
										programs.filter(passesFilters).sort(sortComparer).map(program => (
											<Program key={program.programID} program={program} onDelete={(programId) => {
												setShowDeleteModal(true)
												setProgramToDelete(programId)
											}} />
										)) :
										<div className="p-3 text-center">No programs available</div>
								)
								}
							</div>
						</div>
				}
			</div>
			<Modal centered show={showEnrollModal} onHide={() => setShowEnrollModal(false)}>
				<Modal.Header closeButton>
					When would you like to start?
				</Modal.Header>
				<Modal.Body>
					<label> Pick a date:
						<DatePicker
							selected={enrollDate}
							onChange={(date) => {
								setEnrollDate(new Date(date))
							}}
							className="m-1"
							placeholder="subscribe" />
					</label>
				</Modal.Body>

				<Modal.Footer>
					<Button
						onClick={() => {
							statusDispatch(enrollInProgram({
								start_date: dateToAPIStandard(enrollDate).split(' ')[0],
								program_id: programToAssign
							}),"program","enroll")
							setShowEnrollModal(false)
							setEnrollDate(new Date())
						}}
						disabled={!Boolean(enrollDate)}
						variant={Boolean(enrollDate) ? "outline-success" : "outline-secondary"}>
						{Boolean(enrollDate) ? "Enroll Now" : "Choose Date"}
					</Button>
				</Modal.Footer>
			</Modal>
			<Modal centered show={showCancelModal} onHide={() => setShowCancelModal(false)}>
				<Modal.Header closeButton>
					Cancel Options
				</Modal.Header>
				<Modal.Body>
					<Button
						onClick={() => {
							if (window.confirm('Delete all future events?')) {
								setShowCancelModal(false)
								statusDispatch(cancelProgram({
									future_only: 1,
									assignment_id: programToCancel
								}),"program","cancel")
							}
						}}
						block
						variant="outline-danger"
						size="sm"
						className="mb-4 text-left text-wrap">
						Remove all FUTURE workout, task & nutrition data related to this program
						from your account. You will NOT lose data for completed events.
					</Button>
					<Button
						onClick={() => {
							if (window.confirm('Wipe the entire program?')) {
								setShowCancelModal(false)
								statusDispatch(cancelProgram({
									assignment_id: programToCancel
								}),"program","cancel")
							}
						}}
						block
						variant="outline-danger"
						size="sm"
						className="text-left text-wrap">
						Wipe program from your account. This will delete ALL workout, task & nutrition data related to this program
						from your account. You will lose data for completed events
					</Button>
				</Modal.Body>
			</Modal>

			<SettingsEditor />
			<AssignmentsEditor />
			<AssignModal />
			<TemplatesModal/>
			<CopyModal/>
			<DeleteModal
				onDelete={(future_only) => {
					setShowDeleteModal(false)
					statusDispatch(deleteProgram({
						program_id: programToDelete,
						future_only
					}), "program", "delete")
				}}
				show={showDeleteModal}
				setShow={setShowDeleteModal}
				deleteType="all"
			/>

		</div>
	)
}

export default Programs